import { ChangeDetectionStrategy, Component, computed, effect } from '@angular/core'
import { RouterLink } from '@angular/router'
import { TranslatePipe } from '@ngx-translate/core'
import { SessionStateService } from '../auth/session-state.service'
import { ShortcutCheatSheetState } from './global-shortcuts'
import { comboParts } from './key-combo'
import { ShortcutRegistryService } from './shortcut-registry.service'
import { EffectiveShortcut } from './shortcut.types'

const MODAL_SCOPE = 'modal:shortcut-cheat-sheet'

/** "?" overlay listing the shortcuts that are effective on the current screen. */
@Component({
    selector: 'app-shortcut-cheat-sheet',
    standalone: true,
    imports: [TranslatePipe, RouterLink],
    changeDetection: ChangeDetectionStrategy.OnPush,
    template: `
        @if (state.open()) {
            <div class="ks-backdrop" (click)="close()"></div>
            <div class="ks-panel" role="dialog" aria-modal="true" [attr.aria-label]="'common.keyboard-shortcuts.title' | translate">
                <div class="ks-head">
                    <h3 class="rg title m-0">{{ 'common.keyboard-shortcuts.title' | translate }}</h3>
                    <button type="button" class="rg secondary" (click)="close()" aria-label="Close">
                        <i class="fas fa-times"></i>
                    </button>
                </div>
                @for (group of groups(); track group.name) {
                    <h6 class="ks-group">{{ 'common.keyboard-shortcuts.groups.' + group.name | translate }}</h6>
                    @for (s of group.items; track s.keys) {
                        <div class="ks-row">
                            <span>{{ s.label | translate }}</span>
                            <span class="ks-keys">
                                @for (part of parts(s.keys); track $index) { <kbd>{{ part }}</kbd> }
                            </span>
                        </div>
                    }
                } @empty {
                    <p>{{ 'common.keyboard-shortcuts.none' | translate }}</p>
                }
                @if (settingsUrl(); as url) {
                    <div class="ks-foot">
                        <a [routerLink]="url" (click)="close()">{{ 'common.keyboard-shortcuts.customize' | translate }}</a>
                    </div>
                }
            </div>
        }
    `,
    styles: [`
        .ks-backdrop { position: fixed; inset: 0; background: rgba(0, 0, 0, .35); z-index: 2000; }
        .ks-panel {
            position: fixed; z-index: 2001; top: 10vh; left: 50%; transform: translateX(-50%);
            width: min(560px, calc(100vw - 32px)); max-height: 80vh; overflow: auto;
            background: var(--bs-body-bg, #fff); color: var(--bs-body-color, inherit);
            border-radius: 8px; padding: 16px 20px; box-shadow: 0 10px 30px rgba(0, 0, 0, .25);
        }
        .ks-head { display: flex; justify-content: space-between; align-items: center; margin-bottom: 8px; }
        .ks-group { margin: 14px 0 6px; text-transform: uppercase; font-size: 11px; opacity: .7; letter-spacing: .05em; }
        .ks-row { display: flex; justify-content: space-between; gap: 12px; padding: 4px 0; }
        .ks-keys { display: inline-flex; gap: 4px; white-space: nowrap; }
        kbd { font-size: 12px; padding: 2px 6px; }
        .ks-foot { margin-top: 12px; text-align: right; font-size: 13px; }
    `]
})
export class ShortcutCheatSheetComponent {
    readonly groups = computed(() => {
        // Reading open() re-evaluates `when` guards (which aren't signals) each time it opens
        if (!this.state.open()) return []
        const byGroup = new Map<string, Array<EffectiveShortcut>>()
        for (const s of this.registry.screenEffective()) {
            if (s.when && !s.when()) continue
            const name = s.group ?? (s.layer === 'global' ? 'general' : 'screen')
            byGroup.set(name, [...(byGroup.get(name) ?? []), s])
        }
        return [...byGroup].map(([name, items]) => ({ name, items }))
    })

    constructor (public state: ShortcutCheatSheetState, private registry: ShortcutRegistryService, private session: SessionStateService) {
        // While open, the overlay is the top scope: Esc / ? close it, everything else is blocked
        effect(onCleanup => {
            if (!this.state.open()) return
            const unregister = registry.register(MODAL_SCOPE, [
                { id: 'cheat-sheet.close', label: 'common.keyboard-shortcuts.close', defaultKeys: ['escape', 'shift+slash'], run: () => this.close() }
            ])
            const pop = registry.pushModal(MODAL_SCOPE)
            onCleanup(() => {
                pop()
                unregister()
            })
        })
    }

    /** Settings page of the user's panel, or null outside the workshop/customer panels. */
    settingsUrl (): string | null {
        const panel = this.session.user?.userable_type
        return panel === 'workshop' || panel === 'customer' ? `/${panel}/keyboard-shortcuts` : null
    }

    parts (keys: string) {
        return comboParts(keys)
    }

    close () {
        this.state.open.set(false)
    }
}
